import { IOnePageInfo } from "@/types/types";
import {
  useNavigate,
  useMatches,
  type To,
  type NavigateOptions,
} from "react-router";
import { zipurl } from "@/utils";

/**
 * 需要保留的系统参数列表
 * 新增系统参数时，只需在此数组中添加即可
 */
const SYSTEM_PARAMS = ["ui_hide_nav=true", "ui_hide_menu=true"] as const;

/**
 * 自动保留当前URL中存在的所有系统参数
 * @param path 原始路径
 * @returns 保留系统参数处理后的路径
 */
export const keepSystemParams = (path: string = "") => {
  const currentSearch = window.location.search;
  // 筛选出当前URL中存在的系统参数
  const existingParams = SYSTEM_PARAMS.filter((param) =>
    currentSearch?.includes(param),
  );
  if (existingParams.length > 0) {
    const separator = path.includes("?") ? "&" : "?";
    path = `${path}${separator}${existingParams.join("&")}`;
  }
  return path;
};

/**
 * 自定义路由跳转 Hook, 功能如下:
 * 1. onePage 路由跳转意图;
 * 2. 并保留系统参数;
 * @returns 包装后的navigate函数, 和 react-router navigate 函数完全一致
 */
export const useSmartNavigate = (onePageInfo?: IOnePageInfo) => {
  const navigate = useNavigate();
  const matches = useMatches();
  const matchRouteInfo = matches.slice(-1)?.[0];
  const pageType = (matchRouteInfo?.handle as any)?.pageType;

  return (to: To, options?: NavigateOptions) => {
    // react-router 中 to 可能是 Path 对象, 或者 number like: navigate(-1)
    // 这两种情况均直接透传给 react-router 的 navigate 函数处理
    if (typeof to !== "string") {
      return navigate(to, options);
    }

    let targetPath = to;
    let targetOptions = options || {};

    /********** onepage 跳转处理 start **********/
    if (to === "/") {
      // 指定跳转到"/"根域的情况, 跳过所有onePage逻辑
      targetPath = "/";
    } else if (onePageInfo) {
      // 当前处在onePage容器下 即: basePath / onePage\/Path1 / onePage\/Path2 ...
      // 需要考虑当前所处的任意位置, 为了避免相对路径的行为不一致, 统一使用 /xxx/yyy 这种绝对路径跳转

      const {
        basePath, // 重要信息: 当前onepage页面所处的父页面path（前缀带 / ）
        parentPath, // 重要信息: 当前onepage页面的父级path（前缀带 / ）
        currentPath, // 重要信息: 当前onepage页面的完整path（前缀带 / ）
      } = onePageInfo;

      if (pageType === "lowcode") {
        // 处理 (onePage容器中 && lowcode页面) 的路由跳转意图处理
        // 参考下面普通页面lowcode类型的逻辑, 只处理 /newPath 这种绝对路径跳转, 处理为在新的onePage容器中打开
        if (targetPath.startsWith("/")) {
          // 把 /newPath 转换为 ./newPath, 交给下面的onePage默认跳转意图处理
          targetPath = `.${targetPath}`;
        }
      }
      // onePage 容器中的默认路由跳转意图处理
      if (targetPath.startsWith("/")) {
        // 1. /newPath -> 替换当前onePage容器, 并丢弃之后的onePage容器
        targetPath = `${parentPath}/${zipurl(targetPath.slice(1))}`;
      } else if (targetPath.startsWith("./")) {
        // 2. ./newPath -> 认为需要push下一级容器, 并丢弃下一级之后的onePage容器
        targetPath = `${currentPath}/${zipurl(targetPath.slice(2))}`;
      } else if (targetPath.startsWith("../")) {
        // 取出targetPath中真正的path部分, 即 ? || # 之前的内容
        const targetPathWithoutSearch = targetPath.replace(/[?#].*$/, "");
        if (targetPathWithoutSearch === "../") {
          // 3.1. ../ -> 返回上一级, 丢弃参数和hash
          targetPath = parentPath;
        } else {
          // 3.2. ../newPath -> 替换上一级
          if (parentPath === basePath) {
            // 说明需要操作base层的路由了, 这个时候就不要zip处理了
            targetPath = `/${targetPath.slice(3)}`;
          } else {
            // 替换掉parentPath的最后一级, 并拼接上新的路径(特殊处理了一下/x/y /x/y/ 这种最后以/结尾的逻辑)
            targetPath = parentPath.replace(
              /\/[^\/]*\/?$/,
              `/${zipurl(targetPath.slice(3))}`,
            );
          }
        }
      }
    } else if (pageType === "lowcode") {
      // 普通页面是lowcode类型的情况, 默认启用onePage跳转
      // 需要将原本的 /newPath 跳转转换为相对当前路径的 originPath/newPath 跳转
      // 当前lowcode页面中的router逻辑也是在app-platform中注入的, 目前只注入了 / 逻辑, 除此之外的跳转逻辑都保持原始逻辑
      if (targetPath.startsWith("/")) {
        targetPath = `../${zipurl(targetPath.slice(1))}`;
        // 基于当前router的相对路径跳转, 不论当前是 originPath 还是 originPath/a/b/c, 拼接后永远都是 originPath/newPath
        // TODO: react-router relative 参数的处理逻辑比较隐晦, 目前实测是ok的, 但是为了避免歧义, 后续还是改成绝对路径跳转比较好
        targetOptions.relative = "route";
      }
    }
    /********** onepage 跳转处理 end **********/

    // 保留系统参数
    targetPath = keepSystemParams(targetPath);

    return navigate(targetPath, targetOptions);
  };
};
